/* eslint-disable react-refresh/only-export-components -- vendored shadcn/ui component */
import { cva, type VariantProps } from 'class-variance-authority';
import { Slot } from 'radix-ui';
import * as React from 'react';

import { cn } from '@/_shared/lib/utils';

// Soft tinted chips. Colors mirror the Callout palette (one tint family per
// semantic). `outline` is always bordered; every other variant is borderless by
// default and opts into a matching tinted border via the `bordered` prop.
const badgeVariants = cva(
  'inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded border border-transparent px-1 py-0.5 text-xs font-medium whitespace-nowrap transition-[color,box-shadow] focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 [&>svg]:pointer-events-none [&>svg]:size-3',
  {
    variants: {
      variant: {
        success: 'bg-success-bg text-success',
        error: 'bg-danger-bg text-danger',
        warning: 'bg-warning-bg text-warning',
        info: 'bg-info-bg text-info',
        highlight: 'bg-highlight-bg text-highlight',
        outline: 'border-border text-foreground',
      },
      bordered: {
        true: '',
        false: '',
      },
    },
    compoundVariants: [
      { variant: 'success', bordered: true, class: 'border-success-border' },
      { variant: 'error', bordered: true, class: 'border-danger-border' },
      { variant: 'warning', bordered: true, class: 'border-warning-border' },
      { variant: 'info', bordered: true, class: 'border-info-border' },
      { variant: 'highlight', bordered: true, class: 'border-highlight-border' },
    ],
    defaultVariants: {
      variant: 'info',
      bordered: false,
    },
  },
);

function Badge({
  className,
  variant = 'info',
  bordered = false,
  asChild = false,
  ...props
}: React.ComponentProps<'span'> & VariantProps<typeof badgeVariants> & { asChild?: boolean }) {
  const Comp = asChild ? Slot.Root : 'span';

  return (
    <Comp
      data-slot="badge"
      data-variant={variant}
      className={cn(badgeVariants({ variant, bordered }), className)}
      {...props}
    />
  );
}

export { Badge, badgeVariants };
